import React, { forwardRef, useMemo } from 'react'
import { Chart as ChartJS } from 'chart.js'
import type { ChartOptions, ScriptableContext } from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Line } from 'react-chartjs-2'
import type { Month } from '../../types'
import { axisMax, formatPercent, longMonth, shortMonth } from '../../utils'
import type { GuideDirection } from '../../utils'
import { ChartBox } from './ChartBox'
import { AXIS_FONT, COLOURS, FONTS, monthTicks, percentTick } from './theme'

// The house LineChart registers the annotation plugin globally too; it draws
// nothing unless a chart configures it. Datalabels stays per chart.
ChartJS.register(annotationPlugin)

const PLUGINS = [ChartDataLabels]

// The same on all three trend cards so they line up side by side.
export const TREND_HEIGHT = 240

type Props = {
  points: { month: Month; value: number | null }[]
  guide: number
  // "at_or_under": the good zone is below the guide (debt, spending); otherwise above it.
  direction: GuideDirection
  zoneLabel: string
  label: string
}

// Chart.js's progressive line: each point starts from the one before it, so
// the line draws left to right over 700ms.
type DrawContext = ScriptableContext<'line'>

const drawIn = (count: number): ChartOptions<'line'>['animations'] => {
  const step = 700 / Math.max(count, 1)
  const delay = (context: DrawContext) => (context.type === 'data' && context.mode === 'default' ? context.dataIndex * step : 0)
  // The point before may not exist yet while Chart.js builds the elements, or
  // at all for the first point; those start from the axis.
  const previousY = (context: DrawContext): number => {
    const previous = context.dataIndex > 0 ? context.chart.getDatasetMeta(context.datasetIndex).data[context.dataIndex - 1] : undefined
    if (!previous) return context.chart.scales.y.getPixelForValue(0)
    return (previous.getProps(['y'], true) as { y: number }).y
  }

  return {
    x: { type: 'number', easing: 'linear', duration: step, from: NaN, delay },
    y: { type: 'number', easing: 'linear', duration: step, from: previousY, delay },
  }
}

// A card's average month by month against its guide: a shaded zone on the
// good side, the dashed guide, a label on the first point only and a larger
// dot on the latest.
export const TrendChart = forwardRef<ChartJS<'line'> | undefined, Props>(({ points, guide, direction, zoneLabel, label }, ref) => {
  const [data, animated, still] = useMemo(() => {
    const values = points.map((point) => point.value)
    const numbers = values.filter((value): value is number => value !== null)
    const top = axisMax(Math.max(guide, ...numbers))
    const first = values.findIndex((value) => value !== null)
    const last = values.length - 1 - [...values].reverse().findIndex((value) => value !== null)
    const under = direction === 'at_or_under'
    const radius = (index: number, latest: number, firstPoint: number) => (index === last ? latest : index === first ? firstPoint : 0)

    const chartData = {
      labels: points.map((point) => shortMonth(point.month)),
      datasets: [
        {
          data: values,
          borderColor: COLOURS.forest,
          borderWidth: 3,
          borderCapStyle: 'round' as const,
          borderJoinStyle: 'round' as const,
          spanGaps: true,
          tension: 0,
          pointRadius: values.map((_, index) => radius(index, 7, 4)),
          pointHoverRadius: values.map((_, index) => radius(index, 8, 5)),
          pointBackgroundColor: values.map((_, index) => (index === last ? COLOURS.forest : COLOURS.white)),
          pointBorderColor: values.map((_, index) => (index === last ? COLOURS.white : COLOURS.forest)),
          pointBorderWidth: values.map((_, index) => (index === last ? 2 : 2.5)),
        },
      ],
    }

    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 24, right: 92 } },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => longMonth(points[items[0].dataIndex].month),
            label: (item) => formatPercent(item.parsed.y),
          },
        },
        datalabels: {
          display: (context) => context.dataIndex === first,
          align: -60,
          offset: 6,
          color: COLOURS.ink,
          font: { family: FONTS.heading, size: 13, weight: 600 },
          formatter: (value: number) => `${formatPercent(value)} in ${shortMonth(points[first].month)}`,
        },
        annotation: {
          // The guide's label sits outside the plot, to the right.
          clip: false,
          annotations: {
            zone: {
              type: 'box',
              yMin: under ? 0 : guide,
              yMax: under ? guide : top,
              backgroundColor: COLOURS.zone,
              borderWidth: 0,
              drawTime: 'beforeDatasetsDraw',
              label: {
                display: true,
                content: zoneLabel,
                position: { x: 'start', y: under ? 'end' : 'start' },
                color: COLOURS.forest,
                font: { family: FONTS.heading, size: 12, weight: '600' },
              },
            },
            guide: {
              type: 'line',
              scaleID: 'y',
              value: guide,
              borderColor: COLOURS.grey600,
              borderWidth: 2,
              borderDash: [6, 5],
              drawTime: 'beforeDatasetsDraw',
            },
            guideLabel: {
              type: 'label',
              xValue: points.length - 1,
              yValue: guide,
              position: { x: 'start', y: 'center' },
              xAdjust: 14,
              backgroundColor: 'transparent',
              color: COLOURS.ink,
              content: `${guide}% guide`,
              font: { family: FONTS.heading, size: 13, weight: '600' },
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false, drawBorder: false },
          ticks: monthTicks(points.length),
        },
        y: {
          min: 0,
          max: top,
          grid: { color: (context) => (context.tick.value === 0 ? COLOURS.axis : COLOURS.grid), drawBorder: false, drawTicks: false },
          ticks: { stepSize: top / 2, padding: 10, color: COLOURS.grey600, font: AXIS_FONT, callback: percentTick },
        },
      },
    }

    return [chartData, { ...options, animations: drawIn(points.length) }, { ...options, animation: false as const }]
  }, [points, guide, direction, zoneLabel])

  return (
    <ChartBox height={TREND_HEIGHT} label={label}>
      {(animate) => <Line ref={ref} data={data} options={animate ? animated : still} plugins={PLUGINS} />}
    </ChartBox>
  )
})
